import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Video, Calendar, Upload, X, ArrowRight, Clock, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FirstLookResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [avail, setAvail] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [scheduling, setScheduling] = useState(false);
  // pre-call add-info
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [savingInfo, setSavingInfo] = useState(false);

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/firstlook/${id}`); setLead(res.data); return res.data; }
    catch { return null; }
  }, [id]);

  useEffect(() => {
    let tries = 0, timer;
    const tick = async () => {
      const d = await load();
      // Poll briefly after Stripe redirect until webhook marks paid
      if (d && !d.paid && tries < 8) { tries += 1; timer = setTimeout(tick, 2500); }
    };
    tick();
    return () => clearTimeout(timer);
  }, [load]);

  const paid = !!lead?.paid;
  const scheduledAt = lead?.consultation?.scheduledAt;
  const meetLink = lead?.consultation?.meetLink;
  const precallSubmitted = !!lead?.consultation?.precallSubmitted;

  useEffect(() => {
    if (paid && !scheduledAt && !avail) {
      axios.get(`${API}/firstlook/availability?days=14`).then((r) => setAvail(r.data.days || [])).catch(() => setAvail([]));
    }
  }, [paid, scheduledAt, avail]);

  const pay = async () => {
    setPayLoading(true);
    try {
      const res = await axios.post(`${API}/firstlook/${id}/checkout`, { product: 'consultation', origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch { toast.error('Could not start checkout. Please try again.'); setPayLoading(false); }
  };

  const book = async (slotIso) => {
    setScheduling(true);
    try {
      await axios.post(`${API}/firstlook/${id}/schedule`, { slot: slotIso });
      toast.success('Your consultation is booked!');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not book that time. Please try another.');
      const r = await axios.get(`${API}/firstlook/availability?days=14`).catch(() => null);
      if (r) setAvail(r.data.days || []);
    } finally { setScheduling(false); }
  };

  const submitInfo = async () => {
    if (!notes.trim() && files.length === 0) { toast.info('Add a note or a file first.'); return; }
    setSavingInfo(true);
    try {
      const fd = new FormData();
      fd.append('notes', notes);
      files.forEach((f) => fd.append('photos', f));
      await axios.post(`${API}/firstlook/${id}/precall`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Sent to your CarLens expert.');
      setNotes(''); setFiles([]); await load();
    } catch { toast.error('Could not save. Please try again.'); }
    finally { setSavingInfo(false); }
  };

  const vehicle = lead ? `${lead.year || ''} ${lead.make || ''} ${lead.model || ''}`.trim() : '';

  const StepDot = ({ done, active, label }) => (
    <div className="flex items-center gap-3" data-testid={`fl-step-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${done ? 'bg-emerald-500 text-white' : active ? 'bg-orange-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
        {done ? '✓' : ''}
      </span>
      <span className={`text-sm ${done ? 'text-emerald-300' : active ? 'text-white font-semibold' : 'text-zinc-500'}`}>{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <div className="max-w-4xl mx-auto px-5 py-12 md:py-16">
        {!lead && (
          <div className="flex flex-col items-center py-24 text-center"><Loader2 className="w-10 h-10 text-orange-500 animate-spin" /><p className="mt-4 text-zinc-400">Loading your FirstLook…</p></div>
        )}

        {lead && !paid && (
          <div className="text-center py-16 rounded-2xl border border-orange-500/30 bg-[#161618]" data-testid="firstlook-awaiting-payment">
            <Clock className="w-10 h-10 text-orange-400 mx-auto" />
            <h1 className="mt-4 text-2xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>Complete your booking</h1>
            <p className="mt-2 text-zinc-400 max-w-md mx-auto">Finish checkout to lock in your CarLens FirstLook™ consultation for the {vehicle}.</p>
            <Button onClick={pay} disabled={payLoading} className="mt-6 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-5 h-auto font-semibold" data-testid="firstlook-pay-btn">
              {payLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Pay $49 & Continue'}
            </Button>
          </div>
        )}

        {lead && paid && (
          <div data-testid="firstlook-confirmation">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 mb-5"><CheckCircle2 className="w-7 h-7 text-emerald-400" /></div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk' }} data-testid="firstlook-confirm-h1">
                YOUR FIRSTLOOK STARTS BEFORE THE CALL.
              </h1>
              <p className="mt-3 text-zinc-400 max-w-xl mx-auto">To make the most of your 30-minute consultation, send us the vehicle information before we meet. A CarLens expert will review it ahead of your appointment so we focus on what matters most.</p>
              {vehicle && <p className="mt-2 text-orange-300 font-semibold">{vehicle}</p>}
            </div>

            {/* Status timeline */}
            <div className="mt-8 rounded-2xl border border-zinc-800 bg-[#161618] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-around gap-4" data-testid="firstlook-status">
              <StepDot done={lead.photoCount > 0 || precallSubmitted} active label="Vehicle Information Received" />
              <StepDot done={false} active={!!scheduledAt} label="FirstLook Review In Progress" />
              <StepDot done={!!scheduledAt} active={!scheduledAt} label="Consultation Scheduled" />
            </div>

            {/* Scheduler OR scheduled confirmation */}
            {!scheduledAt ? (
              <div className="mt-8 rounded-2xl border border-orange-500/30 bg-[#161618] p-6" data-testid="firstlook-scheduler">
                <h2 className="flex items-center gap-2 text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}><Calendar className="w-5 h-5 text-orange-400" /> Pick your consultation time</h2>
                <p className="text-sm text-zinc-400 mt-1">30-minute private video call · times shown in Eastern Time.</p>
                {!avail && <div className="py-10 text-center"><Loader2 className="w-6 h-6 text-orange-500 animate-spin mx-auto" /></div>}
                {avail && avail.length === 0 && <p className="mt-4 text-zinc-400">No open times right now — we'll email you to schedule.</p>}
                {avail && avail.length > 0 && (
                  <>
                    <div className="mt-5 flex gap-2 overflow-x-auto pb-2" data-testid="firstlook-days">
                      {avail.map((d, i) => (
                        <button key={d.date} onClick={() => setActiveDay(i)} className={`shrink-0 rounded-xl border px-4 py-2 text-sm ${activeDay === i ? 'border-orange-500 bg-orange-500/10 text-white' : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`} data-testid={`fl-day-${i}`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="firstlook-slots">
                      {(avail[activeDay]?.slots || []).map((s) => (
                        <button key={s.iso} disabled={scheduling} onClick={() => book(s.iso)} className="rounded-lg border border-zinc-700 bg-[#111113] px-3 py-3 text-sm text-white hover:border-orange-500 hover:bg-orange-500/10 disabled:opacity-50 transition" data-testid={`fl-slot-${s.iso}`}>
                          {s.time}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-8 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center" data-testid="firstlook-scheduled">
                <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold">Consultation Scheduled</p>
                <p className="mt-2 text-2xl font-extrabold text-white" style={{ fontFamily: 'Space Grotesk' }}>{lead.consultation?.scheduledLabel || new Date(scheduledAt).toLocaleString()}</p>
                {meetLink ? (
                  <a href={meetLink} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-emerald-700 px-6 py-3 font-semibold" data-testid="firstlook-meet-link"><Video className="w-4 h-4" /> Join Google Meet</a>
                ) : (
                  <p className="mt-3 text-sm text-emerald-100">A CarLens expert will email your private Google Meet link before the call.</p>
                )}
              </div>
            )}

            {/* Pre-call add info */}
            <div className="mt-8 rounded-2xl border border-zinc-800 bg-[#161618] p-6" data-testid="firstlook-precall">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Add vehicle info before your call</h2>
              <p className="text-sm text-zinc-400 mt-1">Have another question, more photos, or a Carfax/AutoCheck report? Add it here so we can review it before your appointment.</p>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Your biggest questions or concerns, seller details, anything else…" className="mt-4 w-full bg-zinc-900/70 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500/70 resize-none" data-testid="fl-precall-notes" />
              <label className="mt-3 flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <Upload className="w-4 h-4 text-orange-400" /> Add photos or history report
                <input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])].slice(0, 10))} data-testid="fl-precall-files" />
              </label>
              {files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full bg-zinc-800 px-3 py-1 text-xs text-zinc-300">{f.name.slice(0, 18)}<button onClick={() => setFiles((p) => p.filter((_, x) => x !== i))}><X className="w-3 h-3" /></button></span>
                  ))}
                </div>
              )}
              <Button onClick={submitInfo} disabled={savingInfo} className="mt-4 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 h-auto font-semibold" data-testid="fl-precall-submit">
                {savingInfo ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send to my CarLens expert <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
              {precallSubmitted && <p className="mt-3 text-sm text-emerald-300 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Vehicle information received — review in progress.</p>}
            </div>

            <p className="mt-8 text-center text-sm text-zinc-500">You're purchasing <span className="text-zinc-300 font-semibold">CarLens FirstLook™</span> — a pre-call vehicle review + live 1-on-1 buying consultation. We do the homework before the call. Then we walk through it together.</p>

            <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 flex items-start gap-2 text-xs leading-6 text-zinc-400" data-testid="firstlook-disclaimer">
              <ShieldCheck className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
              CarLens FirstLook™ is professional guidance based on the information, photos, and documents you provide. It is not a physical inspection. For customers in our Northeast Florida service area, your $49 can be credited toward a full CarLens Signature Inspection — just ask your expert during the call.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstLookResult;
